//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import $ from "jquery";

import {
  IGetAvailableFacebookPagesResponse,
  IGetConnectedFacebookPageTokenResponse,
  IGetFacebookOAuthEndpointResponse,
  IGetFacebookUserTokenResponse,
  IHandleFacebookOAuthResponse,
  IHandleFacebookOAuthResponseRequest,
  ISetConnectedFacebookPageRequest,
} from '@apis/facebook-settings/gatewayFacebookSettingsApiModel';
import { gatewayFetch } from '@utils/sessionFetch';

const gatewayFacebookSettingsFetch = (endpoint: string, init?: RequestInit): Promise<Response> => {
  return gatewayFetch('web_gateway', `/api/v1/settings/facebook${endpoint}`, init);
};

/**
 * Retrieve the most recent non-deleted FacebookUser token from Tokens DB
 *
 * /api/v1/settings/facebook/user-token
 */
export const getFacebookUserToken = async (): Promise<IGetFacebookUserTokenResponse> => {
  const res = await gatewayFacebookSettingsFetch(`/user-token`);

  return await res.json();
};

/**
 * Get the endpoint we'll need to hit to OAuth Facebook for the client
 *
 * /api/v1/settings/facebook/oauth
 *
 * @param isRerequest indicates whether this is a re-auth, defaults to false
 * @deprecated Prefer facebookInstagramSettingsUtils.generateFacebookOAuthUrl()
 */
export const getFacebookOAuthEndpoint = async (
  isRerequest?: boolean,
  redirectPath?: string,
): Promise<IGetFacebookOAuthEndpointResponse> => {
  const params = $.param({
    csrfToken: 'unused',
    isRerequest: !!isRerequest,
    redirectPath,
  });

  const res = await gatewayFacebookSettingsFetch(`/oauth?${ params }`);

  return await res.json();
};

/**
 * Use the Graph API response to complete Facebook OAuth and return a redirect URL
 *
 * /api/v1/settings/facebook/handle-oauth
 *
 * @param request request object with fields returned by the Graph API that are needed to complete OAuth
 */
export const handleFacebookOAuthResponse = async (
  request: IHandleFacebookOAuthResponseRequest
): Promise<IHandleFacebookOAuthResponse> => {
  const res = await gatewayFacebookSettingsFetch(`/handle-oauth?`, {
    body: JSON.stringify(request),
    method: 'POST',
  });

  return await res.json();
};

/**
 * Deauth Facebook for the current client
 *
 * /api/v1/settings/facebook/oauth
 *
 */
export const deauthFacebook = async (): Promise<void> => {
  await gatewayFacebookSettingsFetch(`/oauth`, {
    method: 'DELETE',
  });
};

/**
 * Get all Facebook pages associated with the currently connected user
 *
 * /api/v1/settings/facebook/available-pages
 */
export const getAvailableFacebookPages = async (): Promise<IGetAvailableFacebookPagesResponse> => {
  const res = await gatewayFacebookSettingsFetch(`/available-pages`);

  return await res.json();
};

/**
 * Gets the page token associated with the connected Facebook page for the current client
 *
 * /api/v1/settings/facebook/page-token
 */
export const getConnectedFacebookPageToken = async (): Promise<IGetConnectedFacebookPageTokenResponse> => {
  const res = await gatewayFacebookSettingsFetch(`/page-token`);

  return await res.json();
};

/**
 * Set the connected Facebook Page for the current client
 *
 * /api/v1/settings/facebook/connected-page
 */
export const setConnectedFacebookPage = async (
  request: ISetConnectedFacebookPageRequest,
): Promise<void> => {
  await gatewayFacebookSettingsFetch(`/connected-page`, {
    body: JSON.stringify(request),
    method: 'POST',
  });
};
